// src/pages/HomePage.js
/**
 * Home dashboard shown **after a successful sign-in**.
 *
 * Purpose ──────────────────────────────────────────────────────────
 *  • Acts as a hub for everything the authenticated user can do:
 *      – **New Assessment**  → starts the recording workflow.
 *      – **Questionnaires**  → opens the questionnaire picker.
 *      – **Results**         → (future) list of historical results.
 *
 *  • Keeps the UI deliberately sparse so it loads instantly even on
 *    slow networks (no heavy dependencies, no remote calls).
 *
 * UX / visual layout ───────────────────────────────────────────────
 *  • Tailwind `home-button` utility handles colours / hover states
 *    (defined in `index.css`).
 *  • “New Assessment” icon = filled-in video-camera glyph.
 *
 * @returns {JSX.Element}
 */

import React, { memo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import IFUButton from "../components/navigation/IFUButton";
import { AuthFooter } from "../components/authentication/AuthFooter";
import { trackEvent } from "../utils/analytics/track";
import ModeTileSmall from "../components/recording/ModeTileSmall";
import { useActiveMeasurementSession } from "../recording/session/ActiveMeasurementSessionContext";
import { BreathingGraphSVG } from "../components/recording/BreathingGraphSVG";
import Spinner from "../components/Spinner";
import SmallSpiroChart from "../components/icons/SmallSpiroChart";
import HRChart from "../components/icons/HRChart";
import { RECORDING_MODES } from "../utils/recordingModes";
import SmallHRChart from "../components/icons/SmallHRChart";
import SmallRRChart from "../components/icons/SmallRRChart";
import RespiratoryRate from "../components/icons/RespiratoryRate";
import Pulmonology from "../components/icons/Pulmonology";
import ECGHeart from "../components/icons/ECGHeart";

/* ------------------------------------------------------------------ */
/* Component                                                          */
/* ------------------------------------------------------------------ */
function HomePage({ firstRunByVariant, isPermitted, loadingModes }) {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const { begin } = useActiveMeasurementSession();

  const startModeAndGo = (mode, path, analyticsEvent) => {
    begin(mode);
    if (analyticsEvent) trackEvent(analyticsEvent);
    navigate(path);
  };

  useEffect(() => {
    trackEvent("home_view");
  }, []);

  if (loadingModes) return <Spinner />;

  // const pulsePath = firstRunByVariant["RecordingPulseScan"]
  //   ? "/recording/pulse-scan/guide"
  //   : "/recording/pulse-scan/camera";

  // const spirometryPath = firstRunByVariant["RecordingSpirometry"]
  //   ? "/recording/spirometry/guide"
  //   : "/recording/spirometry/camera";

  // const tidalPath = firstRunByVariant["RecordingTidalBreathing"]
  //   ? "/recording/tidal-breathing/guide"
  //   : "/recording/tidal-breathing/camera";

  const pulsePath = "/recording/pulse-scan/guide";

  const spirometryPath = "/recording/spirometry/guide";

  const tidalPath = "/recording/tidal-breathing/guide";

  return (
    <div className="w-full max-w-lg h-full p-4 flex flex-col justify-end items-center gap-4">
      <div className="h-full w-full flex flex-col justify-start items-center">
        <img
          src="/purpledx_primary_logo.png"
          alt=""
          loading="eager"
          className="max-h-24 w-full object-contain"
        />
        <p className="!text-secondary">{t("labelling.investigationOnly")}</p>
      </div>
      <div className="w-full flex flex-col gap-4">
        <p className="subtitle-text ">{t("home.scans", "Scans")}</p>

        {isPermitted(RECORDING_MODES.SPIROMETRY) && (
          <ModeTileSmall
            to={spirometryPath}
            title={t("levelSelection.spirometryTitle")}
            img={<Pulmonology className="w-fit h-8" />}
            onClick={(e) => {
              e?.preventDefault?.();
              startModeAndGo(
                "spirometry",
                spirometryPath,
                "level_selection_click_spirometry",
              );
            }}
          />
        )}

        {isPermitted(RECORDING_MODES.TIDAL) && (
          <ModeTileSmall
            to={tidalPath}
            title={t("levelSelection.tidalBreathingTitle")}
            img={<RespiratoryRate className="w-fit h-8" />}
            onClick={(e) => {
              e?.preventDefault?.();
              startModeAndGo(
                "tidal-breathing",
                tidalPath,
                "level_selection_click_tidal_breathing",
              );
            }}
          />
        )}

        {isPermitted(RECORDING_MODES.PULSE) && (
          <ModeTileSmall
            to={pulsePath}
            title={t("levelSelection.pulseScanTitle")}
            img={<ECGHeart className="w-fit h-8" />}
            onClick={(e) => {
              e?.preventDefault?.();
              startModeAndGo(
                "pulse-scan",
                pulsePath,
                "level_selection_click_pulse_scan",
              );
            }}
          />
        )}
      </div>
    </div>
  );
}

export default memo(HomePage);
